import { Division, EntityLabel, Site, Subdivision } from '@esg/esg-global-types';
import { auth } from '../google/firebase';
import { apollo_client } from '../app/db_util';
import { ALLOW_DELETE_DIVISION_QUERY, GET_DIVISION_QUERY } from '../graphql/queries/division';
import {
  ARCHIVE_DIVISION,
  CREATE_DIVISION,
  DELETE_DIVISION,
  UNARCHIVE_DIVISION,
  UPDATE_DIVISION
} from '../graphql/mutations/division';

// Singular and plural label for model entity.
export const division_label: EntityLabel = {
  one: 'Division',
  many: 'Divisions'
};

/**
 * Function to query all division documents for current portal company
 * @param {Group} group Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @returns {Array<Division>}
 */
export const getDivisions = async (group_id: string, company_id: string) => {
  try {
    const divisions: Array<Division> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id
        },
        query: GET_DIVISION_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.divisions;
      });
    return divisions;
  } catch (err) {
    const error = `Error while getting Divisions from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : '',
      variables: {
        group_id: group_id,
        company_id: company_id
      }
    })}`;
    throw new Error(`Error: getDivisions: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to create division with relative data
 * @param {string} group_id ID of Group which Division is being created for
 * @param {string} company_id ID of Company which Division is being created for
 * @param {string} division_name Name of new Division
 * @returns {DocumentReference}
 */
export const createDivision = async (
  group_id: string,
  company_id: string,
  division_name: string
) => {
  try {
    if (group_id && company_id && division_name) {
      const new_division: Division = await apollo_client
        .mutate({
          mutation: CREATE_DIVISION,
          variables: {
            group_id: group_id,
            company_id: company_id,
            division_name: division_name,
            user: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.createDivision;
        });
      return new_division;
    }
  } catch (err) {
    const error = `Error while creating Division in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: createDivision: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to update division with relative data
 * @param {string} group_id ID of Group to update division for
 * @param {string} company_id ID of Company to update division for
 * @param {Division} updated_division New Division data to push into document
 * @returns {void}
 */
export const updateDivision = async (
  group_id: string,
  company_id: string,
  updated_division: Division,
  original_division: Division
) => {
  try {
    if (group_id && company_id) {
      await apollo_client
        .mutate({
          mutation: UPDATE_DIVISION,
          variables: {
            group_id: group_id,
            company_id: company_id,
            updated_division: updated_division,
            original_division: original_division,
            user: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.updateDivision;
        });
    }
  } catch (err) {
    const error = `Error while udpating Division in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: updateDivision: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to delete division
 * @param {string} group_id ID of Group to delete division for
 * @param {string} company_id ID of Company to delete division for
 * @param {string} division_id ID of division to delete
 * @param {Array<Subdivision>} delete_subdivisions Child subdivisions to cascade delete
 * @param {Array<Site>} delete_sites Child sites to cascade delete
 * @returns {void}
 */
export const deleteDivision = async (
  group_id: string,
  company_id: string,
  division_id: string,
  division_name: string,
  delete_subdivisions?: Array<Subdivision>,
  delete_sites?: Array<Site>
) => {
  try {
    if (group_id && company_id && division_id) {
      await apollo_client
        .mutate({
          mutation: DELETE_DIVISION,
          variables: {
            group_id: group_id,
            company_id: company_id,
            division_id: division_id,
            division_name: division_name,
            delete_subdivisions: delete_subdivisions,
            delete_sites: delete_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.deleteDivision;
        });
    }
    return;
  } catch (err) {
    const error = `Error while deleting Division in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: deleteDivision: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to archive division
 * @param {string} group_id ID of Group to archive division for
 * @param {string} company_id ID of Company to archive division for
 * @param {string} division_id ID of division to archive
 * @param {Array<Subdivision>} archive_subdivisions Child subdivisions to cascade archive
 * @param {Array<Site>} archive_sites Child sites to cascade archive
 * @returns {void}
 */
export const archiveDivision = async (
  group_id: string,
  company_id: string,
  division_id: string,
  archive_subdivisions?: Array<Subdivision>,
  archive_sites?: Array<Site>
) => {
  try {
    if (group_id && company_id && division_id) {
      await apollo_client
        .mutate({
          mutation: ARCHIVE_DIVISION,
          variables: {
            group_id: group_id,
            company_id: company_id,
            division_id: division_id,
            archive_subdivisions: archive_subdivisions,
            archive_sites: archive_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.archiveDivision;
        });
    }
    return;
  } catch (err) {
    const error = `Error while archiving Division in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: archiveDivision: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to unarchive division
 * @param {string} group_id ID of Group to unarchive division for
 * @param {string} company_id ID of Company to unarchive division for
 * @param {string} division_id ID of division to unarchive
 * @param {Array<Subdivision>} unarchive_subdivisions Child subdivisions to cascade unarchive
 * @param {Array<Site>} unarchive_sites Child sites to cascade unarchive
 * @returns {void}
 */
export const unarchiveDivision = async (
  group_id: string,
  company_id: string,
  division_id: string,
  unarchive_subdivisions?: Array<Subdivision>,
  unarchive_sites?: Array<Site>
) => {
  try {
    if (group_id && company_id && division_id) {
      await apollo_client
        .mutate({
          mutation: UNARCHIVE_DIVISION,
          variables: {
            group_id: group_id,
            company_id: company_id,
            division_id: division_id,
            unarchive_subdivisions: unarchive_subdivisions,
            unarchive_sites: unarchive_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.unarchiveDivision;
        });
    }
    return;
  } catch (err) {
    const error = `Error while unarchiving Division in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: unarchiveDivision: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to check if a Division can be safely deleted
 * @param {string} group_id ID of Group to check Division for
 * @param {string} company_id ID of Company to check Division for
 * @param {string} division_id ID of Division to check
 * @returns {boolean}
 */
export const allowDeleteDivision = async (
  group_id: string,
  company_id: string,
  division_id: string
) => {
  try {
    const allow_delete: boolean = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          division_id: division_id
        },
        query: ALLOW_DELETE_DIVISION_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.allowDeleteDivision;
      });

    return allow_delete;
  } catch (err) {
    const error = `Error while checking Division for deletion: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: allowDeleteDivision: ${JSON.stringify(error)}.`);
  }
};
